import AddIcon from "@material-ui/icons/Add"
import RemoveIcon from "@material-ui/icons/Remove"
import ReplayIcon from "@material-ui/icons/Replay"
import { LatLngBounds } from "leaflet"
import type { LatLng, Map as LeafletMap } from "leaflet"
import type { ReactElement } from "react"
import { useEffect, useMemo, useState } from "react"
import { useMap } from "react-leaflet"
import { connect } from "react-redux"
import type { Dispatch } from "redux"

import { config } from "../../../config"
import { mapViewerDecreaseZoom, mapViewerIncreaseZoom, mapViewerSetZoom } from "../../../redux"
import type { AppState } from "../../../redux"
import { LeafletBottomRight, isAppleDevice } from "../../../utils"
import { MapControl, MapControlContainer, MapFullscreenControl, TooltipIconButton } from "../../atoms"

import "./map-zoom-controls.styles.scss"
import { useTranslation } from "react-i18next"

export interface IMapZoomControlsOwnProps {
  corner: LatLng
  oppositeCorner: LatLng
  mapId: string
  fullscreenButton?: boolean | undefined
  hidden?: boolean
  ignoreResize?: boolean | undefined
}

export interface IMapZoomControlsStateProps {
  zoom?: number
  increaseZoom: (mapId: string) => void
  decreaseZoom: (mapId: string) => void
  setZoom: (mapId: string, zoom?: number) => void
}

function MapZoomControlsView(props: IMapZoomControlsStateProps & IMapZoomControlsOwnProps): ReactElement {
  const {
    mapId,
    corner,
    oppositeCorner,
    zoom,
    increaseZoom,
    decreaseZoom,
    setZoom,
    fullscreenButton,
    hidden,
    ignoreResize
  } = props
  const { t } = useTranslation("general")

  const mapReference: LeafletMap = useMap()

  const mapBounds: LatLngBounds = useMemo(() => {
    return new LatLngBounds(corner, oppositeCorner)
  }, [corner, oppositeCorner])
  const [resizeObserver, setResizeObserver] = useState<ResizeObserver>()
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    return () => {
      setZoom(mapId)
      mapReference.removeEventListener("zoomend")
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    mapReference.on("zoomend", () => {
      setZoom(mapId, mapReference.getZoom())
    })
    if (mapReference) {
      const initialZoom: number = mapReference.getZoom()
      if (initialZoom) {
        setZoom(mapId, initialZoom)
        mapReference.setMaxZoom(initialZoom + config.maxZoom)
        mapReference.setMinZoom(initialZoom - config.minZoom)
      }
    }
    if (mapReference && !resizeObserver) {
      const observer: ResizeObserver = new ResizeObserver(onContainerResize)
      observer.observe(mapReference.getContainer())
      setResizeObserver(observer)
    }
  }, [mapReference])
  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO in a later PR
  useEffect(() => {
    if (zoom && zoom !== mapReference.getZoom()) {
      mapReference.setZoom(zoom)
    }
  }, [zoom])

  function onContainerResize(): void {
    if (!ignoreResize) {
      mapReference.invalidateSize()
      mapReference.fitBounds(mapBounds)
    }
  }

  function resetView(): void {
    mapReference.flyToBounds(mapBounds)
  }

  function handleIncreaseZoom(): void {
    if (zoom !== undefined) increaseZoom(mapId)
  }

  function handleDecreaseZoom(): void {
    if (zoom !== undefined) decreaseZoom(mapId)
  }

  return (
    <MapControlContainer className={`map-zoom-controls ${hidden ? "hidden" : ""}`} position={LeafletBottomRight}>
      <MapControl>
        <TooltipIconButton
          onClick={handleIncreaseZoom}
          placement="left"
          tooltip={t("zoomButtons.zoomIn")}
          icon={<AddIcon />}
        />
        <TooltipIconButton
          onClick={handleDecreaseZoom}
          placement="left"
          tooltip={t("zoomButtons.zoomOut")}
          icon={<RemoveIcon />}
        />
        <TooltipIconButton
          onClick={resetView}
          placement="left"
          tooltip={t("zoomButtons.restoreView")}
          icon={<ReplayIcon />}
        />
      </MapControl>
      {fullscreenButton && !isAppleDevice() && <MapFullscreenControl />}
    </MapControlContainer>
  )
}

function mapStateToProps(state: AppState, ownProps: IMapZoomControlsOwnProps) {
  return {
    zoom: state.mapViewer.zoom.get(ownProps.mapId)
  }
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    increaseZoom: (mapId: string) => dispatch(mapViewerIncreaseZoom(mapId)),
    decreaseZoom: (mapId: string) => dispatch(mapViewerDecreaseZoom(mapId)),
    setZoom: (mapId: string, zoom?: number) => dispatch(mapViewerSetZoom(mapId, zoom))
  }
}

export const MapZoomControls = connect(mapStateToProps, mapDispatchToProps)(MapZoomControlsView)
